import {useGrpcRequestMutation} from '@aboutyou/data-fetcher';
import {CoinsService_SubmitMissionProgressLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coins/v1/service.grpc.lazy';
import {
  SubmitMissionProgressRequest,
  SubmitMissionProgressResponse,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coins/v1/service.types';
import {useCustomerSession} from '@aboutyou/user';
import {useIsMissionsActive} from '../../../features/Coins/utils/useIsMissionsActive';
import {useSuccessMissionState} from '../../../features/Coins/utils/useSuccessMissionState';
import {useGetWallet} from '../../Wallet/utils/useGetWallet';
import {MissionType} from './missionType';
import {useGetActiveMissions} from './useGetActiveMissions';

export function useSubmitMissionProgress({
  mission,
}: {
  mission: 'likeBrandMission';
}): (brandId: number) => Promise<SubmitMissionProgressResponse>;

export function useSubmitMissionProgress({
  mission,
}: {
  mission: Exclude<MissionType, 'likeBrandMission'>;
}): () => Promise<SubmitMissionProgressResponse>;

export function useSubmitMissionProgress({mission}: {mission: MissionType}) {
  const customerSession = useCustomerSession();
  const isMissionsActive = useIsMissionsActive();
  const {refetch: refetchActiveMissions} = useGetActiveMissions();
  const {refetch: refetchWallet} = useGetWallet();
  const disabled = !isMissionsActive || !customerSession;
  const {setMissionSuccessNotification} = useSuccessMissionState();

  const mutation = useGrpcRequestMutation(
    CoinsService_SubmitMissionProgressLazy,
    {
      disabled,
      onSuccess: data => {
        refetchActiveMissions();
        refetchWallet();

        if (data.missionSuccessNotification) {
          setMissionSuccessNotification({
            missionSuccessNotification: data.missionSuccessNotification,
            shouldBeShownAfterToast: mission !== 'likeBrandMission',
          });
        }
      },
      mockData: async params => {
        const request = params as unknown as SubmitMissionProgressRequest;
        const missionType = request?.mission?.$case ?? 'addBirthdayMission';
        return (
          await import('../__mocks__/submitMissionProgress.mock')
        ).submitMissionProgress(missionType);
      },
    },
  );

  switch (mission) {
    case 'likeBrandMission':
      return async (brandId: number) =>
        await mutation.mutateAsync({
          session: customerSession!,
          mission: {
            $case: 'likeBrandMission',
            likeBrandMission: {
              brandId,
            },
          },
        });
    default:
      return async () =>
        await mutation.mutateAsync({
          session: customerSession!,
          mission: {
            $case: mission,
            [mission]: {},
          } as unknown as NonNullable<SubmitMissionProgressRequest['mission']>,
        });
  }
}
